import React from 'react';
import { useRouter } from 'next/router';

import Heading from '@/components/commons/page-elements/Heading';
import SmartContainer from '@/components/commons/smart_container';
import Link from '@/components/commons/link';
import Button, { ButtonSize, ButtonType } from '@/components/commons/button';
import styles from '@/components/sign-up/SignUp.module.css';

const renderBodyitems = (elements) => {
    if (!elements) return null;
    const output = elements.map((e) => {
        if (Array.isArray(e)) {
            return (
                <div className="flex flex-col sm:flex-row items-start sm:items-center flex-wrap">
                    {renderBodyitems(e)}
                </div>
            );
        }
        if (e.link) {
            return (
                <Link key={e.key} to={e.linkRoute || '/'} target={e.target || 'self'} className={e.style}>
                    {e.linkIcon}
                    <span className={styles.externalLinkLink}>{e.text}</span>
                </Link>
            );
        }
        return <p key={e.key} className={e.style}>{e.text}</p>;
    });
    return output;
};

interface Props {
    readonly cardProps?: { [key: string]: any };
    readonly internalCards?: { [key: string]: any }[];
    readonly externalStyles?: string;
    readonly padding?: number[];
    readonly table?: any;
    readonly disabled?: boolean;
    readonly customElement?: any;
    readonly withoutLogo?: boolean;
    readonly inputAfterBody?: any
    readonly secondaryAction?: () => void;
    readonly onClick?: (route: string) => void;
}

const intCardsContainerClass = 'grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4';
const intCardsClass = 'py-6 px-2 flex flex-col sm:flex-row justify-center sm:justify-between items-center md:items-start bg-lightGray rounded-lg';

function BenefitsCard({
    cardProps = {},
    internalCards = [],
    externalStyles = '',
    padding = null,
    table = null,
    disabled = null,
    customElement = null,
    withoutLogo = false,
    inputAfterBody = null,
    secondaryAction = null,
    onClick = null,
}: Props) {
    const router = useRouter();

    const cardInfo = {
        padding: padding || [4, 4, 4, 4],
        rounded: 'lg',
        backgroundColor: 'cirrusWhite',
        externalStyles: externalStyles || 'my-4 md:mx-6 flex items-center justify-center max-w-[890px]',
        children:
    <div
        className={`flex flex-col md:flex-row justify-between
                items-center ${cardProps.externalStyles}`}
    >
        <div
            className="xl:mt-0"
        >
            {!cardProps.customHeader
                ? (
                    <div className={`flex flex-col sm:flex-row justify-center sm:justify-start ${!cardProps.heading.vertCentered ? 'items-center md:items-start' : 'items-center'}`}>
                        <div className="m-0">{cardProps?.heading?.icon}</div>
                        <Heading
                            size={cardProps.heading.size}
                            bold={cardProps.heading.bold}
                            externalStyles={cardProps.heading.externalStyles}
                        >
                            {cardProps.heading.children}
                        </Heading>
                    </div>
                ) : cardProps.customHeader}
            {customElement}
            {renderBodyitems(cardProps.body)}
            {table}
            {internalCards && (
                <div className={intCardsContainerClass}>
                    {internalCards.map((e) => {
                        if (e.icon) {
                            return (
                                <div key={e.key} className={intCardsClass}>
                                    {e.icon}
                                    <div className="mt-4 sm:mt-0 text-center sm:text-left">
                                        <p className="font-bold text-md">{e.boldText}</p>
                                        <p className="">{e.plainText}</p>
                                    </div>

                                </div>
                            );
                        }
                        return <div className={intCardsClass}>{e.children}</div>;
                    })}
                </div>
            )}
            {inputAfterBody}
            <div className="flex flex-col items-center justify-center">
                {cardProps.customButton}
                {cardProps?.buttons
                            && cardProps.buttons.length !== 0
                            && (
                                <div className={`flex flex-col ${cardProps.buttonsCol ? '' : 'sm:flex-row'} items-center justify-around w-full`}>
                                    {cardProps.buttons.map((button) => (
                                        <Button
                                            key={button.key}
                                            type={button.buttonType || ButtonType.primary}
                                            text={button.buttonText}
                                            size={ButtonSize.base}
                                            full={button.fullSizeButton}
                                            externalStyles={`mt-4 sm:mt-6 flex items-center justify-center text-[16px] ${!button.fullSizeButton ? 'px-4 sm:px-[30px] md:px-[60px]' : ''}`}
                                            onClick={() => {
                                                if (onClick) {
                                                    onClick(button.buttonRoute);
                                                } else {
                                                    router.push({ pathname: button.buttonRoute });
                                                }
                                            }}
                                            disabled={disabled || button.disabled || false}
                                            icon={button.buttonIcon}
                                        />
                                    ))}
                                </div>
                            )}
                {cardProps?.linkText && (
                    cardProps?.linkType === 'function' ? (
                        <button type="button" className={`${styles.externalLinkPlain} mt-4 flex items-center justify-center cursor-pointer`} onClick={() => secondaryAction()}>
                            {cardProps.linkIcon}
                            <span className={styles.externalLinkLink}>{cardProps.linkText}</span>
                        </button>
                    ) : (
                        <Link to={cardProps.linkRoute || '/'} className={`${styles.externalLinkPlain} mt-4 flex items-center justify-center`}>
                            {cardProps.linkIcon}
                            <span className={styles.externalLinkLink}>{cardProps.linkText}</span>
                        </Link>
                    )
                )}
            </div>
        </div>
    </div>,
    };

    return (
        <div className="flex flex-col items-center justify-center m-auto h-full">
            {!withoutLogo && (
                <img
                    className="mx-auto"
                    title="Sixty"
                    alt="Sixty"
                    src="/logo/mark.png"
                />
            )}
            <SmartContainer {...cardInfo} />
        </div>
    );
}

export default BenefitsCard;
